import { useState } from "react";
import { Star, MapPin, Clock, PhoneCall, Navigation, ShieldCheck, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ProviderCard({ provider, testIdPrefix = "provider" }) {
  const [open, setOpen] = useState(false);

  const dialUrl = `tel:${provider.phone.replace(/[^+\d]/g, "")}`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${provider.name} ${provider.address}`
  )}`;

  const handleCall = () => {
    toast.success(`Calling ${provider.name}`);
    window.location.href = dialUrl;
  };

  return (
    <>
      <div
        className="rounded-xl border border-slate-800 bg-[#161E2E] overflow-hidden hover:border-amber-500/50 transition-colors duration-200 group"
        data-testid={`${testIdPrefix}-card-${provider.id}`}
      >
        <div className="relative h-40 overflow-hidden">
          <img
            src={provider.image}
            alt={provider.name}
            className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#161E2E] via-[#161E2E]/40 to-transparent" />
          <div className="absolute top-3 left-3 flex gap-2">
            {provider.verified && (
              <Badge className="bg-cyan-500/90 hover:bg-cyan-500 text-slate-950 font-semibold gap-1 border-0">
                <ShieldCheck className="w-3 h-3" />
                Verified
              </Badge>
            )}
            {provider.open_now && (
              <Badge className="bg-emerald-500/90 hover:bg-emerald-500 text-slate-950 font-semibold border-0">
                Open Now
              </Badge>
            )}
          </div>
          <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-slate-950/80 backdrop-blur-sm px-2.5 py-1 rounded-full border border-slate-700">
            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
            <span className="font-mono font-bold text-sm text-slate-50">
              {provider.rating}
            </span>
            <span className="text-xs text-slate-400">({provider.reviews_count})</span>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-50 leading-tight" data-testid={`${testIdPrefix}-name-${provider.id}`}>
              {provider.name}
            </h3>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{provider.address}</span>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5 text-slate-300">
              <Navigation className="w-3.5 h-3.5 text-amber-500" />
              <span className="font-mono font-semibold">{provider.distance_mi} mi</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-300">
              <Clock className="w-3.5 h-3.5 text-cyan-400" />
              <span className="font-mono">{provider.response_time}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {provider.specialties.slice(0, 3).map((s) => (
              <span
                key={s}
                className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-slate-800 text-slate-300 border border-slate-700"
              >
                {s}
              </span>
            ))}
            {provider.specialties.length > 3 && (
              <span className="px-2 py-0.5 text-[10px] font-mono text-slate-500">
                +{provider.specialties.length - 3}
              </span>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleCall}
              data-testid={`call-btn-${provider.id}`}
              className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold gap-2 h-10"
            >
              <PhoneCall className="w-3.5 h-3.5" />
              Call
            </Button>
            <Button
              onClick={() => setOpen(true)}
              data-testid={`details-btn-${provider.id}`}
              variant="outline"
              className="rounded-full border-slate-700 bg-transparent hover:bg-slate-800 hover:text-amber-400 text-slate-200 gap-1"
            >
              Details
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-lg" data-testid={`provider-modal-${provider.id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight text-slate-50">
              {provider.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {provider.description}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Rating</div>
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="font-bold">{provider.rating}</span>
                  <span className="text-xs text-slate-400">({provider.reviews_count} reviews)</span>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Response</div>
                <div className="mt-1 font-mono text-sm">{provider.response_time}</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Hours</div>
                <div className="mt-1 text-sm">{provider.hours}</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Distance</div>
                <div className="mt-1 font-mono">{provider.distance_mi} mi away</div>
              </div>
            </div>

            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                Services
              </div>
              <div className="flex flex-wrap gap-1.5">
                {provider.specialties.map((s) => (
                  <span
                    key={s}
                    className="px-2.5 py-1 rounded-full text-xs bg-slate-800 border border-slate-700 text-slate-200"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleCall}
                data-testid={`modal-call-${provider.id}`}
                className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 gap-2 h-11"
              >
                <PhoneCall className="w-4 h-4" />
                {provider.phone}
              </Button>
              <Button
                onClick={() => window.open(mapsUrl, "_blank")}
                data-testid={`modal-directions-${provider.id}`}
                variant="outline"
                className="rounded-full border-slate-700 bg-transparent hover:bg-slate-800 gap-2 h-11"
              >
                <Navigation className="w-4 h-4" />
                Directions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
